import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Loader2, Package } from "lucide-react";
import { shopApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { track } from "@/lib/pixels";
import { useCart } from "@/lib/store";

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const orderId = params.get("order_id");
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const nav = useNavigate();
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const s = await shopApi.paymentStatus(sessionId);
        if (cancelled) return;
        if (s.payment_status === "paid") {
          setStatus("paid");
          // Payment confirmed — NOW it's safe to empty the cart and drop the pending coupon.
          clear();
          try { localStorage.removeItem("novalynx_pending_coupon"); } catch { /* ignore */ }
          try {
            const o = await shopApi.order(s.order_id);
            setOrder(o);
            // Fire Purchase pixel ONCE per order (dedupe via sessionStorage)
            const pk = `nx_purchase_fired_${o.order_id}`;
            if (!sessionStorage.getItem(pk)) {
              const total = o?.totals?.total || o?.total || 0;
              track("Purchase", {
                order_id: o.order_id,
                value: total,
                currency: "USD",
                items: (o.items || []).map((i) => ({ id: i.product_id, quantity: i.quantity, price: i.unit_price })),
              });
              sessionStorage.setItem(pk, "1");
            }
          } catch {}
          return;
        }
      } catch {}
      setAttempts((a) => a + 1);
    };
    tick();
    const iv = setInterval(() => { if (attempts < 20 && status !== "paid") tick(); else clearInterval(iv); }, 2000);
    return () => { cancelled = true; clearInterval(iv); };
    // eslint-disable-next-line
  }, [sessionId, attempts]);

  return (
    <div className="max-w-2xl mx-auto px-4 py-24 text-center">
      {status === "paid" ? (
        <>
          <div className="h-16 w-16 rounded-full bg-primary/20 mx-auto flex items-center justify-center mb-6">
            <CheckCircle2 className="h-8 w-8 text-primary" />
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mb-3">Payment received</h1>
          <p className="text-muted-foreground mb-8">A confirmation email is on its way. Your gadgets are being prepared.</p>
          {order && (
            <div className="rounded-2xl bg-card border border-white/5 p-6 text-left" data-testid="order-summary">
              <div className="flex items-center justify-between mb-4"><span className="text-muted-foreground text-sm">Order</span><span className="font-mono">{order.order_id}</span></div>
              <div className="flex items-center justify-between mb-4"><span className="text-muted-foreground text-sm">Tracking</span><span className="font-mono text-primary">{order.tracking_number}</span></div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground"><Package className="h-4 w-4" /> Status: {order.tracking_status}</div>
            </div>
          )}
          <div className="flex gap-3 justify-center mt-8">
            <Button className="btn-primary" onClick={() => nav("/account?tab=orders")} data-testid="view-orders-btn">View my orders</Button>
            <Button variant="outline" className="border-white/15" onClick={() => nav("/shop")}>Continue shopping</Button>
          </div>
        </>
      ) : (
        <>
          <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto mb-6" />
          <h1 className="font-display text-2xl font-bold mb-2">Confirming payment…</h1>
          <p className="text-muted-foreground">Session: <code className="text-xs">{sessionId?.slice(0, 20)}…</code></p>
          {attempts > 15 && <p className="text-sm text-muted-foreground mt-6">Taking longer than usual. <Link to="/account" className="text-primary">Check your orders</Link>.</p>}
        </>
      )}
    </div>
  );
}
